import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Select } from 'antd';
import { useLazyGetOrderLineItemsQuery } from '../../redux/api/order-line-item';
import { useLazyGetOrderLineItemByIdQuery } from '../../redux/api/order';
import { SelectProps } from 'antd/es/select';
import _ from 'lodash';
import { Lineitem } from '../../interfaces/Ordel.Interface';

type SingleValue = string | undefined;
type MultiValue = string[];

interface BatchGroupFilter {
	orderId: string;
	lineItemName: string;
}

interface IProps extends Omit<SelectProps, 'value' | 'onChange' | 'mode'> {
	value?: SingleValue | MultiValue;
	onChange?: (value: SingleValue | MultiValue) => void;
	search?: string;
	showThumbnail?: boolean;
	/** When true (default), appends the building name to each option label. */
	showBuilding?: boolean;
	materialBuildingId?: string | null;
	mode?: 'multiple';
	/**
	 * When true with mode="multiple", only line items on the same order with
	 * the same name can be selected together (batch siblings).
	 */
	restrictToSameOrderAndName?: boolean;
}

const formatLineItemLabel = (
	item: Lineitem,
	showBuilding: boolean
): React.ReactNode => {
	const base = `${item?.order?.name} - ${item.name}`;
	if (!showBuilding) {
		return base;
	}
	return `${base} (${item.building?.name ?? '--'})`;
};

const SalesOrderLineItemSelect: React.FC<IProps> = (props) => {
	const {
		mode,
		value,
		onChange,
		showBuilding: showBuildingProp,
		showThumbnail,
		materialBuildingId,
		restrictToSameOrderAndName = false,
		search,
		...selectProps
	} = props;
	const showBuilding = showBuildingProp ?? true;
	const [soLineItemData, setSoLineItemData] = useState<Lineitem[] | undefined>(
		[]
	);
	const [anchorLineItem, setAnchorLineItem] = useState<Lineitem | null>(null);
	const [getLineItemsData] = useLazyGetOrderLineItemsQuery();
	const [fetchLineItemById] = useLazyGetOrderLineItemByIdQuery();

	const selectedIds = useMemo(
		() => (Array.isArray(value) ? value : value ? [value] : []),
		[value]
	);

	const anchorId =
		restrictToSameOrderAndName && mode === 'multiple' && selectedIds.length
			? selectedIds[0]
			: null;

	const filterLineItemsData = useCallback(
		(data: Lineitem[] | undefined) => {
			if (!materialBuildingId) {
				return data;
			}

			return data?.filter(
				(item) =>
					!item.building?.id || item.building.id === materialBuildingId
			);
		},
		[materialBuildingId]
	);

	const getOrderLineItems = useCallback(async (searchTerm?: string) => {
		const res = await getLineItemsData({
			page_size: 100,
			search: searchTerm || '',
			status: 'Received,In Progress',
		});
		const filtered_data = filterLineItemsData(res?.data?.results);
		setSoLineItemData(filtered_data);
	}, [filterLineItemsData, getLineItemsData]);

	useEffect(() => {
		getOrderLineItems(search);
	}, [getOrderLineItems, search]);

	useEffect(() => {
		if (!anchorId) {
			setAnchorLineItem(null);
			return;
		}

		const fromList = soLineItemData?.find((item) => item.id === anchorId);
		if (fromList?.order?.id) {
			setAnchorLineItem(fromList);
			return;
		}

		if (anchorLineItem?.id === anchorId && anchorLineItem.order?.id) {
			return;
		}

		let cancelled = false;
		void fetchLineItemById({ id: anchorId })
			.unwrap()
			.then((item) => {
				if (!cancelled) {
					setAnchorLineItem(item);
				}
			})
			.catch(() => {
				if (!cancelled) {
					setAnchorLineItem(null);
				}
			});

		return () => {
			cancelled = true;
		};
	}, [anchorId, anchorLineItem, fetchLineItemById, soLineItemData]);

	const batchGroupFilter = useMemo((): BatchGroupFilter | null => {
		if (!restrictToSameOrderAndName || mode !== 'multiple' || !anchorId) {
			return null;
		}
		if (anchorLineItem?.id === anchorId && anchorLineItem.order?.id) {
			return {
				orderId: anchorLineItem.order.id,
				lineItemName: anchorLineItem.name,
			};
		}
		const fromList = soLineItemData?.find((item) => item.id === anchorId);
		if (fromList?.order?.id) {
			return {
				orderId: fromList.order.id,
				lineItemName: fromList.name,
			};
		}
		return null;
	}, [
		anchorId,
		anchorLineItem,
		mode,
		restrictToSameOrderAndName,
		soLineItemData,
	]);

	const debounceSalesOrderItem = useCallback(
		_.debounce((searchTerm: string) => getOrderLineItems(searchTerm), 600),
		[getOrderLineItems]
	);

	const matchesBatchGroup = useCallback(
		(item: Lineitem) => {
			if (!batchGroupFilter) {
				return true;
			}
			if (selectedIds.includes(item.id)) {
				return true;
			}
			return (
				item.order?.id === batchGroupFilter.orderId &&
				item.name === batchGroupFilter.lineItemName
			);
		},
		[batchGroupFilter, selectedIds]
	);

	const visibleOptions = useMemo(() => {
		if (!restrictToSameOrderAndName || mode !== 'multiple') {
			return soLineItemData;
		}
		return soLineItemData?.filter(matchesBatchGroup);
	}, [
		matchesBatchGroup,
		mode,
		restrictToSameOrderAndName,
		soLineItemData,
	]);

	const handleChange = (nextValue: SingleValue | MultiValue) => {
		if (mode !== 'multiple' || !restrictToSameOrderAndName) {
			onChange?.(nextValue);
			return;
		}

		const ids = (nextValue as MultiValue) ?? [];
		if (!ids.length) {
			onChange?.([]);
			return;
		}

		if (!batchGroupFilter) {
			onChange?.(ids);
			return;
		}

		const allowedIds = ids.filter((id) => {
			if (
				selectedIds.includes(id) &&
				!soLineItemData?.some((item) => item.id === id)
			) {
				return true;
			}
			const item = soLineItemData?.find((entry) => entry.id === id);
			if (!item) {
				return true;
			}
			return (
				item.order?.id === batchGroupFilter.orderId &&
				item.name === batchGroupFilter.lineItemName
			);
		});

		onChange?.(allowedIds);
	};

	return (
		<Select
			allowClear
			placeholder={
				mode === 'multiple' ? 'Select line item(s)' : 'Select line item'
			}
			showSearch
			onSearch={debounceSalesOrderItem}
			filterOption={false}
			value={value}
			onChange={handleChange}
			optionLabelProp="label"
			mode={mode}
			{...selectProps}
		>
			{visibleOptions?.map((item, index) => (
				<Select.Option
					key={index}
					value={item.id}
					label={formatLineItemLabel(item, showBuilding)}
				>
					<div className="flex flex-row items-center gap-2">
						{showThumbnail && (
							<img src={item.thumbnail_file} width="48px" height="48px" />
						)}
						{formatLineItemLabel(item, showBuilding)}
					</div>
				</Select.Option>
			))}
		</Select>
	);
};

export default SalesOrderLineItemSelect;
